import React, { useMemo, useCallback, useState, useEffect } from 'react'
import classNames from 'classnames'

import { useGetTeamName } from '../../hooks'
import {
  getArrayPairs,
  getArrayFours,
  getScore,
  getClassScoreNew,
  createPlaceholderDataMobile,
  findRoundIndexForMatch,
} from '../../utils'

import s from './styles.module.scss'

const MobilePlayOff = ({
  data,
  tournamentMember,
  setMatchItem,
  setOpenBracketModal,
  openBracketModal,
  isAllowRoundUpdate,
  setOpenRoundChange,
  openRoundChange,
  setTimeRound,
  setRoudnId,
  nextMatchId,
  nextLoserMatchId,
  winnerMatchRef,
  loserMatchRef,
}) => {
  const [selectedRoundIndex, setSelectedRoundIndex] = useState(0)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const getTeamName = useGetTeamName(tournamentMember)

  // Switch to the round containing the next match when nextMatchId or nextLoserMatchId changes
  useEffect(() => {
    const targetMatchId = nextMatchId || nextLoserMatchId
    if (targetMatchId) {
      const targetRoundIndex = findRoundIndexForMatch(targetMatchId, data)
      setSelectedRoundIndex(targetRoundIndex)
    }
  }, [nextMatchId, nextLoserMatchId, data])

  const handleMatchClick = useCallback(
    teamItem => {
      setMatchItem(teamItem[0])
      setOpenBracketModal(!openBracketModal)
    },
    [openBracketModal, setMatchItem, setOpenBracketModal]
  )

  // Get available rounds for selection (both Upper and Lower rounds)
  const availableRounds = useMemo(() => {
    if (!data?.basket?.length) return []

    const rounds = []

    // Add Upper rounds (basket[0]) - include rounds even if no data
    if (data.basket[0]?.cols) {
      const upperRounds = data.basket[0].cols
        .filter(colsItem => colsItem?.roundName) // Only filter by roundName, not rows length
        .map(colsItem => ({ ...colsItem, type: 'upper' }))
      rounds.push(...upperRounds)
    }

    // Add Lower rounds (basket[1]) - include rounds even if no data
    if (data.basket[1]?.cols) {
      const lowerRounds = data.basket[1].cols
        .filter(colsItem => colsItem?.roundName) // Only filter by roundName, not rows length
        .map(colsItem => ({ ...colsItem, type: 'lower' }))
      rounds.push(...lowerRounds)
    }

    return rounds
  }, [data])

  // Get current selected round data
  const currentRound = availableRounds[selectedRoundIndex]

  if (!data?.basket?.length) {
    return <div className={s.noData}>No data</div>
  }

  return (
    <div className={s.mobileContainer}>
      {/* Round Selection */}
      <div className={s.roundSelection}>
        <div className={s.roundSelectionLabel}>SELECT ROUND</div>
        <div className={s.roundSelectContainer}>
          <div
            className={s.roundSelect}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <span>{availableRounds[selectedRoundIndex]?.roundName}</span>
            <div className={s.roundSelectArrow}>
              {isDropdownOpen ? '▲' : '▼'}
            </div>
          </div>
          {isDropdownOpen && (
            <div className={s.roundDropdown}>
              {availableRounds.map((colsItem, index) => (
                <div
                  key={`round-${index}`}
                  className={classNames(
                    s.roundOption,
                    selectedRoundIndex === index && s.roundOptionSelected
                  )}
                  onClick={() => {
                    setSelectedRoundIndex(index)
                    setIsDropdownOpen(false)
                    if (colsItem && isAllowRoundUpdate) {
                      setOpenRoundChange(!openRoundChange)
                      setTimeRound(
                        colsItem?.rows[0]?.round?.startedAt,
                        colsItem?.rows[0]?.round?.mode
                      )
                      setRoudnId(colsItem?.rows[0]?.round?.id)
                    }
                  }}
                >
                  {colsItem?.roundName}
                  {selectedRoundIndex === index && (
                    <span className={s.roundOptionCheckmark}>✓</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Selected Round Matches with teamsBlock structure */}
      {currentRound && (
        <div className={s.matchesList}>
          {/* Render both Upper and Lower Round matches with the same mobile UI structure */}
          {getArrayFours(createPlaceholderDataMobile(currentRound))?.map(
            (el, teamsBlockIndex) => (
              <div
                key={`teams-block-${teamsBlockIndex}`}
                className={s.teamsBlock}
              >
                {getArrayPairs(el)?.map((teamItem, teamBlockIndex) => (
                  <div
                    key={`team-block-${teamBlockIndex}`}
                    className={s.teamBlock}
                    onClick={() =>
                      teamItem[0]?.matchId && handleMatchClick(teamItem)
                    }
                  >
                    {teamItem?.map((block, blockIndex) => (
                      <div
                        key={`block-${block?.id}-${blockIndex}`}
                        ref={
                          nextMatchId === teamItem[0]?.matchId
                            ? winnerMatchRef
                            : nextLoserMatchId === teamItem[0]?.matchId
                              ? loserMatchRef
                              : null
                        }
                        className={classNames(
                          s.matchItem,
                          (nextMatchId === teamItem[0]?.matchId ||
                            nextLoserMatchId === teamItem[0]?.matchId) &&
                            s.matchItemHighlighted
                        )}
                      >
                        <div className={s.matchNumber}>{block?.id}</div>
                        <div className={s.player}>
                          <span className={s.playerTitle}>
                            {getTeamName(block)}
                          </span>
                          <div
                            className={classNames(
                              s.playerPont,
                              getClassScoreNew(
                                teamItem,
                                block,
                                s.playerPont_orange
                              )
                            )}
                          >
                            {getScore(block)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      )}
    </div>
  )
}

export default React.memo(MobilePlayOff)
